"use client";

import { useEffect } from "react";
import axios from "axios";
import { ProductCard } from "@/components/product/product-card";
import { useRecentlyViewedStore } from "@/store/recently-viewed.store";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

interface RecentlyViewedProps {
  excludeId?: string;
  excludeSlug?: string;
  className?: string;
}

// Recently-viewed items are a client-side snapshot in localStorage, so they
// survive a server-side catalog reset and can point at products that no longer
// exist. Validate the stored slugs against the API once per session and prune
// the orphans. Module-level guard keeps the chatty check from re-running on
// every mount (the section renders site-wide in the footer).
let orphanCheckDone = false;

export function RecentlyViewed({ excludeId, excludeSlug, className }: RecentlyViewedProps) {
  const allItems = useRecentlyViewedStore((s) => s.items);
  const removeIds = useRecentlyViewedStore((s) => s.removeIds);
  const items = allItems
    .filter((p) => p.id !== excludeId && p.slug !== excludeSlug)
    .slice(0, 4);

  useEffect(() => {
    if (orphanCheckDone || allItems.length === 0) return;
    orphanCheckDone = true;

    let cancelled = false;
    (async () => {
      const checks = await Promise.all(
        allItems.map(async (p) => {
          try {
            await api.get(`/api/catalog/products/${p.slug}`);
            return null;
          } catch (e) {
            if (axios.isAxiosError(e) && e.response?.status === 404) return p.id;
            return null; // network/other error: keep the item, retry next session
          }
        }),
      );
      const missing = checks.filter((id): id is string => id !== null);
      if (!cancelled && missing.length > 0) removeIds(missing);
    })();

    return () => {
      cancelled = true;
    };
  }, [allItems, removeIds]);

  if (items.length === 0) return null;

  return (
    <div className={cn("mt-10 border-t border-[var(--es-border-soft)] pt-8 md:mt-16 md:pt-12", className)}>
      <h2 className="mb-5 text-xl font-bold tracking-tight text-[var(--sf-foreground)] md:mb-6">Son Görüntülenenler</h2>
      <div className="grid grid-cols-2 items-stretch gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {items.map((product, index) => (
          <ProductCard
            key={product.id}
            product={product}
            index={index}
          />
        ))}
      </div>
    </div>
  );
}
